import { ApiService } from '../../api/api-service';
import { PaginatedResponse, SortParams, Technician, TechnicianFilters } from '../../types';
import { sanitizeFilters } from '../../utils/filter-sanitizer';

const SERVICE_ROOT = '/technicians';

/**
 * Service for technician-related API operations
 */
export class TechnicianService extends ApiService {
  /**
   * Get a paginated list of technicians
   * @param page Page number
   * @param pageSize Number of items per page
   * @param filters Optional filters
   * @param sort Optional sort parameters
   * @returns Promise resolving to the paginated technician data
   */
  async getTechnicians(
    page: number,
    pageSize: number,
    filters?: TechnicianFilters,
    sort?: SortParams
  ): Promise<PaginatedResponse<Technician>> {
    return this.handleApiCall(async () => {
      // Sanitize filters: remove undefined/null/empty and convert known booleans
      const apiFilters = filters ? sanitizeFilters(filters as Record<string, unknown>) : {};

      return this.client.get(SERVICE_ROOT, {
        params: {
          page,
          pageSize,
          ...apiFilters,
          ...sort,
        },
      });
    }, 'Error fetching technicians');
  }

  /**
   * Get active technicians for a specific manufacturer (shop floor scope)
   * @param manufacturerId Manufacturer ID
   * @returns Promise resolving to the list of active technicians
   */
  async getActiveTechniciansByManufacturer(manufacturerId: number): Promise<Technician[]> {
    return this.handleApiCall(async () => {
      return this.client.get(`${SERVICE_ROOT}/active-shop-floor/${manufacturerId}`);
    }, `Error fetching active technicians for manufacturer ${manufacturerId}`);
  }

  /**
   * Get a technician by ID
   * @param id Technician ID
   * @returns Promise resolving to the technician data
   */
  async getTechnicianById(id: string): Promise<Technician> {
    return this.handleApiCall(async () => {
      return this.client.get(`${SERVICE_ROOT}/${id}`);
    }, `Error fetching technician with ID ${id}`);
  }

  /**
   * Create a new technician
   * @param technician Technician data
   * @returns Promise resolving to the created technician
   */
  async createTechnician(technician: Omit<Technician, 'id'>): Promise<Technician> {
    return this.handleApiCall(
      () => this.client.post(SERVICE_ROOT, technician),
      'Error creating technician'
    );
  }

  /**
   * Update a technician
   * @param id Technician ID
   * @param technician Technician data to update
   * @returns Promise resolving to the updated technician
   */
  async updateTechnician(id: string, technician: Partial<Technician>): Promise<void> {
    return this.handleApiCall(
      () => this.client.put(`${SERVICE_ROOT}/${id}`, technician),
      `Error updating technician with ID ${id}`
    );
  }

  /**
   * Delete a technician
   * @param id Technician ID
   * @returns Promise resolving when the technician is deleted
   */
  async deleteTechnician(id: string): Promise<void> {
    return this.handleApiCall(async () => {
      return this.client.delete(`${SERVICE_ROOT}/${id}`);
    }, `Error deleting technician with ID ${id}`);
  }

  /**
   * Validate if technician initials are unique for a manufacturer
   * @param technicianId Optional technician ID to exclude from check (for updates)
   * @param initials Initials to validate
   * @param manufacturerId Manufacturer ID
   * @returns Promise resolving to isUnique boolean
   */
  async validateInitialsUniqueness(
    technicianId: number | null,
    initials: string,
    manufacturerId: number
  ): Promise<boolean> {
    const result = await this.handleApiCall<{ isUnique: boolean }>(
      () =>
        this.client.get(`${SERVICE_ROOT}/validate-initials-uniqueness`, {
          params: {
            technicianId: technicianId || undefined,
            initials,
            manufacturerId,
          },
        }),
      `Error validating initials uniqueness for manufacturer ${manufacturerId}`
    );
    return result.isUnique;
  }
}
